import React, { useEffect, useRef } from 'react';

import { convertToHalfWidthKatakana } from '../utils/validations/common-validation';

/**
 * Custom hook to handle katakana conversion on blur
 * @param value - Current field value
 * @param onUpdate - Function to update the field value
 * @param fieldName - Name of the field for debugging
 */
export const useKatakanaConversion = (
  value: string,
  onUpdate: (newValue: string) => void,
  fieldName: string
) => {
  const previousValueRef = useRef<string>(value);
  const isUpdatingRef = useRef<boolean>(false);

  useEffect(() => {
    // Skip if we're currently updating to avoid infinite loops
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
      return;
    }

    // Only convert if the value has changed and contains full-width katakana
    if (value !== previousValueRef.current) {
      const convertedValue = convertToHalfWidthKatakana(value);

      if (convertedValue !== value) {
        isUpdatingRef.current = true;
        onUpdate(convertedValue);
      }

      previousValueRef.current = value;
    }
  }, [value, onUpdate, fieldName]);

  return {
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const currentValue = e.target.value;
      const convertedValue = convertToHalfWidthKatakana(currentValue);

      if (convertedValue !== currentValue) {
        onUpdate(convertedValue);
      }
    },
  };
};
